import { useCallback, useEffect, useRef, useState } from 'react';
import { codigoDoErro, mensagemDoErro } from '../../api/erros.js';

/**
 * O que se sabe do QR de um código da lista (produto ou lote).
 *
 * `carregando` não é `falhou`: enquanto a resposta não chega, o cartão diz que
 * está gerando, e nunca mostra a etiqueta em branco como se fosse pronta.
 * `codigo` é o do erro da API — é por ele que a tela decide se vale tentar de
 * novo (um lote que não cabe no QR não passa a caber na segunda vez).
 */
export type EstadoDoQrDe<Q> =
  | { readonly situacao: 'carregando' }
  | { readonly situacao: 'pronto'; readonly qr: Q }
  | { readonly situacao: 'falhou'; readonly mensagem: string; readonly codigo?: string };

export type QrsPorCodigo<Q> = {
  readonly estados: ReadonlyMap<string, EstadoDoQrDe<Q>>;
  readonly tentarDeNovo: (codigo: string) => void;
};

function comEstado<Q>(
  atuais: ReadonlyMap<string, EstadoDoQrDe<Q>>,
  codigo: string,
  estado: EstadoDoQrDe<Q>,
): ReadonlyMap<string, EstadoDoQrDe<Q>> {
  return new Map(atuais).set(codigo, estado);
}

/**
 * Pede ao middleware a matriz de cada código da lista, uma vez por código.
 *
 * Tirar um código da lista não descarta a matriz já obtida: se ele voltar, a
 * etiqueta reaparece sem nova ida ao servidor. Falha fica no próprio código —
 * um recusado não impede os outros de imprimirem.
 *
 * `obter` precisa ser estável (função de módulo ou `useCallback`).
 */
export function useQrPorCodigo<Q>(
  codigos: readonly string[],
  obter: (codigo: string) => Promise<Q>,
): QrsPorCodigo<Q> {
  const [estados, definirEstados] = useState<ReadonlyMap<string, EstadoDoQrDe<Q>>>(() => new Map());
  const [tentativa, definirTentativa] = useState(0);
  const pedidos = useRef(new Set<string>());
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    const novos = codigos.filter((codigo) => !pedidos.current.has(codigo));

    for (const codigo of novos) {
      pedidos.current.add(codigo);
      definirEstados((atuais) => comEstado<Q>(atuais, codigo, { situacao: 'carregando' }));

      obter(codigo).then(
        (qr) => {
          if (montado.current) {
            definirEstados((atuais) => comEstado<Q>(atuais, codigo, { situacao: 'pronto', qr }));
          }
        },
        (erro: unknown) => {
          if (montado.current) {
            definirEstados((atuais) =>
              comEstado<Q>(atuais, codigo, {
                situacao: 'falhou',
                mensagem: mensagemDoErro(erro),
                codigo: codigoDoErro(erro),
              }),
            );
          }
        },
      );
    }
  }, [codigos, obter, tentativa]);

  const tentarDeNovo = useCallback((codigo: string) => {
    pedidos.current.delete(codigo);
    definirTentativa((anterior) => anterior + 1);
  }, []);

  return { estados, tentarDeNovo };
}
